/**
 * VEDANGJYOTISHAM V6.0 — 12 LIFE DOMAINS CANONICAL QUESTION REGISTRY & EVALUATION ENGINE
 * 
 * Centralized, extensible question framework where questions are distinct analytical lenses
 * operating over the SINGLE common Jyotish Calculation & Evidence core.
 */

import { CompleteKundali, Language } from '../../types/kundali';
import { LifeThemeId } from '../../types/advancedLifeAnalysis';
import {
  DynamicQuestionDefinition,
  DynamicQuestionAnswer,
  DomainCategoryGroup,
  QuestionCategoryId,
  AIQuestionMappingResult
} from '../../types/lifeDomainQuestions';

import { CANONICAL_QUESTIONS_REGISTRY } from './canonicalQuestionRegistry';
import { mapNaturalLanguageQuestion } from './aiQuestionMapper';

export { mapNaturalLanguageQuestion } from './aiQuestionMapper';

// ============================================================================
// 1. THE CANONICAL DYNAMIC QUESTION REGISTRY
// ============================================================================

export const DYNAMIC_QUESTION_REGISTRY: DynamicQuestionDefinition[] = CANONICAL_QUESTIONS_REGISTRY;

// ============================================================================
// 2. REGISTRY SEARCH, COVERAGE & DISCOVERY FUNCTIONS
// ============================================================================

export function getAllDynamicQuestions(): DynamicQuestionDefinition[] {
  return DYNAMIC_QUESTION_REGISTRY;
}

export function getDomainCanonicalQuestions(domainId: LifeThemeId): {
  primaryQuestions: DynamicQuestionDefinition[];
  specializedQuestions: DynamicQuestionDefinition[];
} {
  const domainQuestions = DYNAMIC_QUESTION_REGISTRY.filter(q => q.domainId === domainId);
  return {
    primaryQuestions: domainQuestions.filter(q => !q.isSpecialized),
    specializedQuestions: domainQuestions.filter(q => q.isSpecialized)
  };
}

export function getDomainQuestionCategories(domainId: LifeThemeId): DomainCategoryGroup[] {
  const domainQuestions = DYNAMIC_QUESTION_REGISTRY.filter(q => q.domainId === domainId);
  const categoryMap: Record<string, DynamicQuestionDefinition[]> = {};

  for (const q of domainQuestions) {
    if (!categoryMap[q.categoryId]) {
      categoryMap[q.categoryId] = [];
    }
    categoryMap[q.categoryId].push(q);
  }

  const groups: DomainCategoryGroup[] = [];
  for (const [catId, questions] of Object.entries(categoryMap)) {
    groups.push({
      categoryId: catId as QuestionCategoryId,
      categoryNameEn: questions[0].categoryNameEn,
      categoryNameHi: questions[0].categoryNameHi,
      questions
    });
  }

  return groups;
}

export function searchDynamicQuestions(
  query: string,
  domainId?: LifeThemeId
): {
  directMatches: DynamicQuestionDefinition[];
  aiMappingResult?: AIQuestionMappingResult;
} {
  const cleanQuery = query.trim().toLowerCase();
  if (!cleanQuery) {
    const questions = domainId
      ? DYNAMIC_QUESTION_REGISTRY.filter(q => q.domainId === domainId)
      : DYNAMIC_QUESTION_REGISTRY;
    return { directMatches: questions };
  }

  // 1. First run AI natural language understanding
  const aiMapping = mapNaturalLanguageQuestion(query, domainId);

  // 2. Keyword & Text matching
  const directMatches = DYNAMIC_QUESTION_REGISTRY.filter(q => {
    if (domainId && q.domainId !== domainId) {
      return false;
    }

    const matchesEn = q.questionEn.toLowerCase().includes(cleanQuery);
    const matchesHi = q.questionHi.toLowerCase().includes(cleanQuery);
    const matchesKeywords = q.keywords.some(k => k.toLowerCase().includes(cleanQuery));
    const matchesCatEn = q.categoryNameEn.toLowerCase().includes(cleanQuery);
    const matchesCatHi = q.categoryNameHi.toLowerCase().includes(cleanQuery);
    const matchesLabel =
      q.shortLabelEn.toLowerCase().includes(cleanQuery) ||
      q.shortLabelHi.toLowerCase().includes(cleanQuery);
    const matchesIntent = q.canonicalIntent.toLowerCase().includes(cleanQuery);

    return matchesEn || matchesHi || matchesKeywords || matchesCatEn || matchesCatHi || matchesLabel || matchesIntent;
  });

  // If AI mapping found a confident match that isn't in direct matches, prepend it
  if (aiMapping.confidence !== 'LOW') {
    const matchedDef = DYNAMIC_QUESTION_REGISTRY.find(q => q.id === aiMapping.matchedQuestionId);
    if (matchedDef && !directMatches.some(m => m.id === matchedDef.id)) {
      directMatches.unshift(matchedDef);
    }
  }

  return {
    directMatches,
    aiMappingResult: aiMapping
  };
}

export function evaluateDynamicQuestion(
  questionId: string,
  kundali: CompleteKundali,
  lang: Language = 'en',
  options?: { focusDate?: Date | string }
): DynamicQuestionAnswer {
  const def = DYNAMIC_QUESTION_REGISTRY.find(q => q.id === questionId) || DYNAMIC_QUESTION_REGISTRY[0];
  return def.evaluate(kundali, lang, options);
}

/**
 * Question Coverage Matrix Report for Verification & QA
 */
export interface DomainCoverageReport {
  domainId: LifeThemeId;
  totalQuestions: number;
  primaryQuestionsCount: number;
  specializedQuestionsCount: number;
  categoriesCount: number;
  implemented: boolean;
  mapped: boolean;
  uiVisible: boolean;
  hasDuplicates: boolean;
}

export function getQuestionCoverageMatrix(): DomainCoverageReport[] {
  const domainIds: LifeThemeId[] = [
    'career_job',
    'business',
    'wealth_finance',
    'marriage_relationship',
    'children',
    'education',
    'house_property',
    'vehicle',
    'health_vitality',
    'foreign_travel',
    'litigation_competition',
    'spirituality',
    'family_parents'
  ];

  return domainIds.map(domId => {
    const questions = DYNAMIC_QUESTION_REGISTRY.filter(q => q.domainId === domId);
    const primary = questions.filter(q => !q.isSpecialized);
    const specialized = questions.filter(q => q.isSpecialized);
    const categories = new Set(questions.map(q => q.categoryId));

    // Duplicate intent check
    const intents = questions.map(q => q.canonicalIntent);
    const hasDuplicates = new Set(intents).size !== intents.length;

    return {
      domainId: domId,
      totalQuestions: questions.length,
      primaryQuestionsCount: primary.length,
      specializedQuestionsCount: specialized.length,
      categoriesCount: categories.size,
      implemented: questions.length > 0,
      mapped: questions.every(q => typeof q.evaluate === 'function'),
      uiVisible: true,
      hasDuplicates
    };
  });
}
